import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { DbEvent } from '../types';
import { RESTROOM_FEE, INSURANCE_WAIVER_FEE } from '../lib/pricing';

const EVENT_TYPES = ['Birthday Party', 'Concert', 'DJ Night', 'Private Party', 'Other'];
const STATUSES = ['upcoming', 'completed', 'cancelled'] as const;

export function EditEvent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<DbEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form fields
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [eventType, setEventType] = useState('');
  const [estimatedAttendance, setEstimatedAttendance] = useState('');
  const [pricingModel, setPricingModel] = useState<'flat_rate' | 'revenue_share'>('flat_rate');
  const [restroomProvided, setRestroomProvided] = useState(true);
  const [insuranceOption, setInsuranceOption] = useState<'policy' | 'waiver_fee'>('policy');
  const [status, setStatus] = useState<'upcoming' | 'completed' | 'cancelled'>('upcoming');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!id) return;
    supabase.from('events').select('*, clients(*)').eq('id', id).single().then(({ data }) => {
      if (data) {
        setEvent(data);
        setEventDate(data.event_date);
        setStartTime(data.start_time);
        setEventType(data.event_type ?? 'Birthday Party');
        setEstimatedAttendance(data.estimated_attendance ? String(data.estimated_attendance) : '');
        setPricingModel(data.pricing_model);
        setRestroomProvided(data.restroom_provided);
        setInsuranceOption(data.insurance_option);
        setStatus(data.status);
        setNotes(data.notes ?? '');
      }
      setLoading(false);
    });
  }, [id]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!eventDate) errs.eventDate = 'Event date is required';
    if (!startTime) errs.startTime = 'Start time is required';
    const [h] = startTime.split(':').map(Number);
    if (h < 18) errs.startTime = 'Start time must be 6:00 PM or later';
    if (estimatedAttendance && parseInt(estimatedAttendance) > 450) errs.attendance = 'Maximum attendance is 450 people';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !id) return;
    setSaving(true);

    // Recalculate payment due date if event date changed
    const eventDateObj = new Date(eventDate + 'T12:00:00');
    const due = new Date(eventDateObj);
    due.setDate(due.getDate() + 30);
    const dueDateStr = due.toISOString().split('T')[0];

    const restroomFee = restroomProvided ? 0 : RESTROOM_FEE;
    const insuranceFee = insuranceOption === 'waiver_fee' ? INSURANCE_WAIVER_FEE : 0;

    const { error } = await supabase.from('events').update({
      event_date: eventDate,
      start_time: startTime,
      event_type: eventType,
      estimated_attendance: estimatedAttendance ? parseInt(estimatedAttendance) : null,
      pricing_model: pricingModel,
      restroom_provided: restroomProvided,
      restroom_fee: restroomFee,
      insurance_option: insuranceOption,
      insurance_fee: insuranceFee,
      status,
      notes: notes || null,
      payment_due_date: dueDateStr,
    }).eq('id', id);

    setSaving(false);
    if (!error) navigate(`/events/${id}`);
  };

  if (loading) return (
    <div className="flex justify-center items-center py-24">
      <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!event) return (
    <div className="max-w-lg mx-auto px-4 py-12 text-center text-gray-500">Event not found.</div>
  );

  const inputClass = "w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 bg-white";

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <button onClick={() => navigate(`/events/${id}`)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={16} /> Back to Event
      </button>

      <h1 className="font-serif text-2xl font-bold text-gray-900 mb-1">Edit Event</h1>
      <p className="text-sm text-gray-500 mb-6">
        Client: <strong>{event.clients?.name}</strong>
      </p>

      <div className="space-y-5">

        {/* Event Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Event Date *</label>
          <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} className={inputClass} />
          {errors.eventDate && <p className="text-xs text-red-500 mt-1">{errors.eventDate}</p>}
        </div>

        {/* Start Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Time * (6:00 PM or later)</label>
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} min="18:00" className={inputClass} />
          {errors.startTime && <p className="text-xs text-red-500 mt-1">{errors.startTime}</p>}
          <p className="text-xs text-gray-400 mt-1">End time is always 12:30 AM</p>
        </div>

        {/* Event Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
          <select value={eventType} onChange={e => setEventType(e.target.value)} className={inputClass}>
            {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>

        {/* Estimated Attendance */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Attendance (max 450)</label>
          <input
            type="number" value={estimatedAttendance} onChange={e => setEstimatedAttendance(e.target.value)}
            min="1" max="450" placeholder="e.g. 150" className={inputClass}
          />
          {errors.attendance && <p className="text-xs text-red-500 mt-1">{errors.attendance}</p>}
        </div>

        {/* Pricing Model */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Pricing Model</label>
          <div className="grid grid-cols-2 gap-3">
            {([['flat_rate', 'Flat Rate', '$1,500'], ['revenue_share', 'Revenue Share', '$1,000 + 50%']] as const).map(([val, label, sub]) => (
              <button key={val} type="button" onClick={() => setPricingModel(val)}
                className={`p-3 rounded-xl border-2 text-left transition-colors ${pricingModel === val ? 'border-gold-500 bg-gold-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <p className="text-sm font-semibold text-gray-900">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Restroom */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-900 mb-3">Portable Restroom</p>
          <div className="flex gap-3">
            {([true, false] as const).map(val => (
              <button key={String(val)} type="button" onClick={() => setRestroomProvided(val)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${restroomProvided === val ? 'border-gold-500 bg-gold-50 text-gold-700' : 'border-gray-200 text-gray-600'}`}>
                {val ? 'Client provides (+$0)' : `No restroom (+$${RESTROOM_FEE})`}
              </button>
            ))}
          </div>
        </div>

        {/* Insurance */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-900 mb-3">Event Insurance</p>
          <div className="space-y-2">
            {([['policy', "Client's own policy (+$0)"], ['waiver_fee', `Pay waiver via Zelle (+$${INSURANCE_WAIVER_FEE})`]] as const).map(([val, label]) => (
              <button key={val} type="button" onClick={() => setInsuranceOption(val)}
                className={`w-full py-2 px-3 rounded-lg text-sm font-medium border-2 text-left transition-colors ${insuranceOption === val ? 'border-gold-500 bg-gold-50 text-gold-700' : 'border-gray-200 text-gray-600'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Event Status</label>
          <div className="flex gap-2">
            {STATUSES.map(s => (
              <button key={s} type="button" onClick={() => setStatus(s)}
                className={`flex-1 py-2 rounded-xl text-sm font-medium border-2 capitalize transition-colors ${
                  status === s
                    ? s === 'upcoming' ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : s === 'completed' ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-400 bg-gray-100 text-gray-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 resize-none"
            placeholder="Any notes about this event…" />
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-600 text-white py-3 rounded-xl font-semibold transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : <><Check size={16} /> Save Changes</>}
        </button>
      </div>
    </div>
  );
}
